// src/components/layout/ChatColumn/actions/HistoryRestart.jsx
// ==============================================
// actions/HistoryRestart.jsx
// Handles restarting the chat history from a specific message
// Uses the integrated AuthManager for settings.
// ==============================================
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Check } from 'lucide-react';
import { HousePortal } from '@/App';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '@/managers/ThemeProvider';
import { useConversation } from '@/managers/ConvoManager';

/* ========================
 * RestartHistoryModal - Modal component for history restart confirmation
 * ======================== */
const RestartHistoryModal = ({ onConfirm, onCancel, isProcessing }) => {
    const { isLightTheme } = useTheme();
    const [createSummary, setCreateSummary] = useState(false);

    const handleConfirm = () => {
        onConfirm(createSummary);
    };

    return (
        <HousePortal>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-glass">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className={`
                        ${isLightTheme
                            ? 'bg-surface text-on-surface border border-border-color'
                            : 'bg-surface bg-opacity-80 border border-white/5'
                        }
                        backdrop-blur-glass rounded-glass p-6 shadow-glass-md max-w-sm w-full
                        relative
                    `}
                >
                    {isProcessing && (
                        <div className="absolute inset-0 bg-surface/90 backdrop-blur-sm flex items-center justify-center rounded-glass">
                            <div className="text-center">
                                <motion.div
                                    className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                />
                                <p className={`
                                    text-lg font-medium
                                    ${isLightTheme ? 'text-on-surface' : 'text-white'}
                                `}>
                                    Processing...
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-2 mb-4">
                        <AlertTriangle className="text-warning" size={20} />
                        <h2 className={`
                            text-lg font-semibold
                            ${isLightTheme ? 'text-on-surface' : 'text-white'}
                        `}>
                            Reset Conversation Start Point
                        </h2>
                    </div>
                    <p className={`
                        mb-4
                        ${isLightTheme ? 'text-on-surface/70' : 'text-white/70'}
                    `}>
                        This will permanently delete all messages BEFORE this point. Future conversations will only have context from this message forward.
                    </p>
                    <p className={`
                        mb-4 text-sm
                        ${isLightTheme ? 'text-warning/80' : 'text-warning'}
                    `}>
                        This action cannot be undone.
                    </p>
                    <div className="mt-4">
                        <label
                            className={`
                                flex items-start gap-3 p-3 rounded-lg border cursor-pointer
                                ${isLightTheme
                                    ? 'bg-surface-dark border-border-color/30'
                                    : 'bg-surface/30 border-white/5'
                                }
                            `}
                        >
                            <input
                                type="checkbox"
                                checked={createSummary}
                                onChange={(e) => setCreateSummary(e.target.checked)}
                                disabled={isProcessing}
                                className="sr-only peer"
                            />
                            <div className={`
                                relative w-4 h-4 border-2 rounded
                                transition-all duration-200
                                ${isLightTheme
                                    ? 'border-border-color peer-checked:border-primary'
                                    : 'border-white/20 peer-checked:border-primary'
                                }
                                ${createSummary ? 'bg-primary' : 'bg-transparent'}
                                ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}>
                                <Check
                                    size={12}
                                    className={`
                                        absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                                        transition-opacity duration-200
                                        ${createSummary ? 'opacity-100' : 'opacity-0'}
                                        text-white
                                    `}
                                />
                            </div>
                            <div className="flex-1">
                                <span
                                    className={`
                                        block text-sm font-medium
                                        ${isLightTheme ? 'text-on-surface/90' : 'text-white/90'}
                                    `}
                                >
                                    Create Conversation Summary
                                </span>
                                <p className={`
                                    text-sm mt-1
                                    ${isLightTheme ? 'text-on-surface/60' : 'text-white/60'}
                                `}>
                                    A summary of your conversation up to this point will be generated and added as the first message in your new thread, providing condensed historical context.
                                </p>
                            </div>
                        </label>
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onCancel}
                            disabled={isProcessing}
                            className={`
                                px-4 py-2 rounded-md hover:bg-surface transition-colors
                                ${isLightTheme
                                    ? 'bg-surface-dark text-on-surface'
                                    : 'bg-surface-dark text-white/70'}
                                ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isProcessing}
                            className={`
                                px-4 py-2 text-white rounded-md
                                transition-colors
                                ${isProcessing
                                    ? 'bg-surface-dark opacity-50 cursor-not-allowed'
                                    : 'bg-warning hover:bg-warning-dark'
                                }
                            `}
                        >
                            {isProcessing ? 'Processing...' : 'Reset Start Point'}
                        </button>
                    </div>
                </motion.div>
            </div>
        </HousePortal>
    );
};

/* ========================
 * HistoryRestart - Main component for history restart functionality
 * Manages state and logic for restarting history from a message.
 * ======================== */
const HistoryRestart = ({ show, onClose, messageId }) => {
    const { restartHistoryFromMessage } = useConversation();
    const [isProcessing, setIsProcessing] = useState(false);

    const performRestart = async (createSummary) => {
        if (isProcessing) return;
        setIsProcessing(true);

        try {
            const success = await restartHistoryFromMessage(messageId, createSummary);
            if (success) {
                onClose();
            } else {
                // Optional: Show an error toast to the user
                console.error('Error during restart: Operation failed');
            }
        } catch (error) {
            console.error('Error during restart:', error);
            // Optional: Show an error toast to the user
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <AnimatePresence>
            {show && (
                <RestartHistoryModal
                    onConfirm={performRestart}
                    onCancel={onClose}
                    isProcessing={isProcessing}
                />
            )}
        </AnimatePresence>
    );
};

RestartHistoryModal.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
};

HistoryRestart.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    messageId: PropTypes.string.isRequired,
};

export default HistoryRestart;